import React, {useEffect, useState} from "react";
import Customer from "../types/Customer";
import useCustomerByDomainGet from "../hooks/useCustomerByDomainGet";
import CustomerTranslation from "../types/CustomerTranslation";
import {CUSTOMER_THEMES, LANG_CODE_MAP, LANG_ID_MAP, LANG_NAME_MAP} from "../constants";
import useChangeLanguage from "../../core/languages/hooks/useChangeLanguage";
import useCurrentLanguage from "../../core/languages/hooks/useCurrentLanguage";
import Loader from "../../ui/shared/Loader/Loader";

interface CustomerContextProps {
    languageId: number,
    languageCode: string,
    setLanguageId: (languageId: number) => void,
    customer: Customer,
    customerTranslation: CustomerTranslation;
    theme: any;
}

export const CustomerContext = React.createContext<CustomerContextProps>({} as any);

const CustomerContextProvider = ({children}:{children:React.ReactNode}) => {
    const currentLang = useCurrentLanguage();
    const { data: customer } = useCustomerByDomainGet(window.location.hostname);
    const [languageId, setLanguageId] = useState(0);
    const [languageCode, setLanguageCode] = useState('');
    const [customerTranslation, setCustomerTranslation] = useState<CustomerTranslation | null>(null);

    const changeLang = useChangeLanguage();

    useEffect(() => {
        if (!customer) return;

        customer.language = customer.languages.map((language: any) => {
            return {
                code: LANG_CODE_MAP[language.substr(11)],
                name: LANG_NAME_MAP[language.substr(11)],
            };
        });

        if (!localStorage.getItem('customerWasLoaded')) {
            const newLangCode = customer.language[0].code;
            setLanguageId(LANG_ID_MAP[newLangCode]);
            setLanguageCode(newLangCode);
            changeLang(newLangCode);
            localStorage.setItem('customerWasLoaded', '1');
        }

        setCustomerTranslation({
            id: customer.id,
            info: customer.info[languageCode],
            footer: customer.footer[languageCode],
            legalNotice: customer.legalNotice[languageCode],
            privacyPolicy: customer.privacyPolicy[languageCode],
            cookiePolicy: customer.cookiePolicy[languageCode],
            contractTerms: customer.contractTerms[languageCode],
            paymentRequiredDisclaimer: customer.paymentRequiredDisclaimer ? customer.paymentRequiredDisclaimer[languageCode] : null,
        });

    }, [customer, currentLang, languageCode]);

    useEffect(() => {
        setLanguageId(LANG_ID_MAP[currentLang]);
        setLanguageCode(currentLang);
    }, [currentLang]);

    const theme = customer ? CUSTOMER_THEMES[customer.type + ""] || null : null;

    return (
        customer && customerTranslation ?
            (
                <CustomerContext.Provider value={{
                    languageId,
                    languageCode,
                    setLanguageId,
                    customer,
                    customerTranslation,
                    theme
                }}>
                    {children}
                </CustomerContext.Provider>
            )
            : (
                <div className="flex items-center justify-center w-full h-screen">
                    <Loader />
                </div>
            )
    );
};

export default CustomerContextProvider;
